import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { activateClickOnSpace } from "../../../utils/keyboardAccessibility"

const DOUGHNUT_ROTATION_DEGREES = 0
const DOUGHNUT_CIRCUMFERENCE_DEGREES = 360
const COLOR_SCHEME_CHANGE_EVENT = "railsui:color-scheme-change"
const EMPTY_TRACK_SEGMENT = { value: 0, color: "var(--ds-chart-track-bg)" }
const CANVAS_ZERO_SEGMENT_VALUE = 1.0
const KEYBOARD_HINT =
  "Use Left and Right arrow keys to move between segments. Home and End jump to the first and last segment."

const ARC_LAYOUT = {
  rotation: DOUGHNUT_ROTATION_DEGREES,
  circumference: DOUGHNUT_CIRCUMFERENCE_DEGREES
}

const NAVIGATION_KEYS = {
  ArrowRight: (index, length) => (index + 1) % length,
  ArrowDown: (index, length) => (index + 1) % length,
  ArrowLeft: (index, length) => (index - 1 + length) % length,
  ArrowUp: (index, length) => (index - 1 + length) % length,
  Home: () => 0,
  End: (_, length) => length - 1
}

export default class extends Controller {
  static targets = ["canvas", "region", "live"]

  static values = {
    chartType: { type: String, default: "donut" },
    segments: Array,
    cutout: { type: String, default: "90%" },
    accessibleSegments: Array,
    keyboardAccessible: { type: Boolean, default: true }
  }

  connect() {
    this.activeIndex = 0
    this.handleColorSchemeChange = this.handleColorSchemeChange.bind(this)
    window.addEventListener(COLOR_SCHEME_CHANGE_EVENT, this.handleColorSchemeChange)
    this.silenceLiveRegion()
    this.renderChart()
  }

  disconnect() {
    if (this.themeFrameId) cancelAnimationFrame(this.themeFrameId)
    window.removeEventListener(COLOR_SCHEME_CHANGE_EVENT, this.handleColorSchemeChange)
    this.destroyChart()
  }

  handleColorSchemeChange() {
    this.themeFrameId = requestAnimationFrame(() => {
      this.themeFrameId = null
      this.updateChartTheme()
    })
  }

  handleFocus() {
    this.enableLiveRegion()
    this.announceSegment(this.activeIndex, { initial: true })
  }

  navigate(event) {
    if (!this.keyboardAccessibleValue) return

    const segments = this.navigationSegments
    const advance = NAVIGATION_KEYS[event.key]
    if (!segments.length || !advance) return

    event.preventDefault()
    this.enableLiveRegion()
    this.activeIndex = advance(this.activeIndex, segments.length)
    this.announceSegment(this.activeIndex)
  }

  activateLegendLink(event) {
    activateClickOnSpace(event)
  }

  updateChartTheme() {
    if (!this.isDonutChart) return

    const colors = this.resolvedSegmentColors()
    if (!this.chart) {
      this.renderChart()
      return
    }

    this.chart.data.datasets[0].backgroundColor = colors
    this.chart.update("none")
  }

  updatePercent(percent) {
    if (!this.isDonutChart) return

    const clamped = Math.min(100, Math.max(0, Math.round(Number(percent) || 0)))
    const remainder = Math.max(0, 100 - clamped)
    const fill = "var(--ds-chart-fill-brand)"
    const track = "var(--ds-chart-track-bg)"
    const segments = [{ value: clamped > 0 ? clamped : CANVAS_ZERO_SEGMENT_VALUE, color: fill }]

    if (remainder > 0) {
      segments.push({ value: remainder, color: track })
    }

    this.segmentsValue = segments
    this.renderChart()
  }

  silenceLiveRegion() {
    if (!this.hasLiveTarget) return

    this.liveTarget.setAttribute("aria-live", "off")
  }

  enableLiveRegion() {
    if (!this.hasLiveTarget) return

    this.liveTarget.setAttribute("aria-live", "polite")
  }

  announceSegment(index, { initial = false } = {}) {
    if (!this.hasLiveTarget) return

    const segments = this.navigationSegments
    const segment = segments[index]
    if (!segment) return

    if (initial && segments.length > 1) {
      this.liveTarget.textContent = KEYBOARD_HINT
      return
    }

    this.liveTarget.textContent = `${segment.announcement}. Segment ${index + 1} of ${segments.length}.`
  }

  renderChart() {
    if (!this.isDonutChart) {
      console.warn(`aquifer-ui-chart: unsupported chartType "${this.chartTypeValue}" (only "donut" is implemented)`)
      return
    }

    this.destroyChart()
    this.syncCanvasDimensions()

    const segments = this.chartSegments()
    this.chart = new Chart(this.canvasTarget, {
      type: "doughnut",
      data: { datasets: [this.donutDataset(segments)] },
      options: this.donutChartOptions()
    })
  }

  get isDonutChart() {
    return this.chartTypeValue === "donut"
  }

  get navigationSegments() {
    return this.accessibleSegmentsValue?.length ? this.accessibleSegmentsValue : []
  }

  chartSegments() {
    return this.segmentsValue?.length ? this.segmentsValue : [EMPTY_TRACK_SEGMENT]
  }

  resolvedSegmentColors(segments = this.chartSegments()) {
    return segments.map((segment) => this.resolveColor(segment.color))
  }

  donutDataset(segments) {
    return {
      data: segments.map((segment) => segment.value),
      backgroundColor: this.resolvedSegmentColors(segments),
      borderWidth: 0,
      spacing: 0,
      ...ARC_LAYOUT
    }
  }

  donutChartOptions() {
    return {
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      cutout: this.cutoutValue,
      ...ARC_LAYOUT,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      }
    }
  }

  destroyChart() {
    this.chart?.destroy()
    this.chart = null
  }

  syncCanvasDimensions() {
    const canvas = this.canvasTarget
    if (!canvas) return

    const displayWidth = canvas.offsetWidth || Number.parseInt(canvas.style.width, 10) || 0
    const displayHeight = canvas.offsetHeight || Number.parseInt(canvas.style.height, 10) || 0
    if (!displayWidth || !displayHeight) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.round(displayWidth * dpr)
    canvas.height = Math.round(displayHeight * dpr)
  }

  resolveColor(color) {
    if (!color || typeof color !== "string") return color

    const trimmed = color.trim()
    if (!trimmed.startsWith("var(")) return trimmed

    const property = trimmed.slice(4, -1).trim()
    const resolved = getComputedStyle(this.element).getPropertyValue(property).trim()
    return resolved || trimmed
  }
}
